import { User } from 'fitify-types/src/types/user'
import { isDefined } from 'fitify-utils/src/properties'

import { DEFAULT_PROMO_CODE } from 'data/promo'
import type { FunnelRoutesDefinition } from 'types/routes'
import { type DataPathState, Funnel, Question } from 'types/state'

import { getSubscriptionFlowPages } from '../../utils/funnel-pages'

type CHF01DataState = DataPathState<Funnel.CHF01>

const pages = {
  welcomeAgeBuckets: {
    path: '/chf01/welcome-age-buckets',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    isExcludedFromProgressBarOrStepper: true,
    stateKey: 'age_bucket',
    id: 'welcome_age_buckets',
  },
  socialProofMillions: {
    path: '/chf01/social-proof-millions',
    isValidatedStep: true,
    id: 'social_proof_millions',
  },
  experience: {
    path: '/chf01/experience',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'experience',
    id: 'experience',
  },
  experiencePitch: {
    path: '/chf01/experience-pitch',
    isValidatedStep: true,
    id: 'experience_pitch',
  },
  mainGoal: {
    path: '/chf01/main-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'goal',
    id: 'main_goal',
    required: true,
  },
  goalPitch: {
    path: '/chf01/goal-pitch',
    isValidatedStep: true,
    id: 'goal_pitch',
    condition: (data: CHF01DataState) => {
      return isDefined(data.goal)
    },
  },
  motivationEarly: {
    path: '/chf01/motivation-early',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'motivation_early',
    id: 'motivation_early',
  },
  didYouKnowHormones: {
    path: '/chf01/did-you-know-hormones',
    isValidatedStep: true,
    id: 'did_you_know_hormones',
  },
  didYouKnowDepression: {
    path: '/chf01/did-you-know-depression',
    isValidatedStep: true,
    id: 'did_you_know_depression',
  },
  didYouKnowLongevity: {
    path: '/chf01/did-you-know-longevity',
    isValidatedStep: true,
    id: 'did_you_know_longevity',
  },
  physicalBuild: {
    path: '/chf01/physical-build',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'current_shape',
    id: 'physical_build',
  },
  bodyGoal: {
    path: '/chf01/body-goal',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'body_goal',
    id: 'body_goal',
  },
  weightChangeExp: {
    path: '/chf01/weight-change-exp',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'weight_change_exp',
    id: 'weight_change_exp',
  },
  bodyTypePitch: {
    path: '/chf01/body-type-pitch',
    isValidatedStep: true,
    id: 'body_type_pitch',
    condition: (data: CHF01DataState) => {
      return isDefined(data.weight_change_exp)
    },
  },
  bestShape: {
    path: '/chf01/best-shape',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'latest_ideal_weight',
    id: 'best_shape',
  },
  consistencyPitch: {
    path: '/chf01/consistency-pitch',
    isValidatedStep: true,
    id: 'consistency_pitch',
  },
  flexibility: {
    path: '/chf01/flexibility',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'flexibility',
    id: 'flexibility',
  },
  posturePitch: {
    path: '/chf01/posture-pitch',
    isValidatedStep: true,
    id: 'posture_pitch',
  },
  problemAreas: {
    path: '/chf01/problem-areas',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'problem_areas',
    id: 'problem_areas',
    required: true,
  },
  struggle: {
    path: '/chf01/struggle',
    cluster: 'cluster_my_profile',
    isValidatedStep: true,
    stateKey: 'struggle',
    id: 'struggle',
  },
  strugglePitch: {
    path: '/chf01/struggle-pitch',
    isValidatedStep: true,
    id: 'struggle_pitch',
    condition: (data: CHF01DataState) => {
      return Boolean(
        data.struggle?.length &&
          data.struggle.some((item) => item !== Question.Struggle.None)
      )
    },
  },
  stairsCardio: {
    path: '/chf01/stairs-cardio',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'stairs_cardio',
    id: 'stairs_cardio',
  },
  howOftenExercise: {
    path: '/chf01/how-often-exercise',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_exercise',
    id: 'how_often_exercise',
  },
  exerciseBarrier: {
    path: '/chf01/exercise-barrier',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'exercise_barrier',
    id: 'exercise_barrier',
    condition: (data: CHF01DataState) => {
      const howOften = data.how_often_exercise
      return (
        howOften === Question.HowOftenExercise.Never ||
        howOften === Question.HowOftenExercise.Monthly
      )
    },
  },
  workoutLength: {
    path: '/chf01/workout-length',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'workout_length',
    id: 'workout_length',
  },
  bigResultsPitch: {
    path: '/chf01/big-results-pitch',
    isValidatedStep: true,
    id: 'big_results_pitch',
  },
  howOftenWalks: {
    path: '/chf01/how-often-walks',
    cluster: 'cluster_activity',
    isValidatedStep: true,
    stateKey: 'how_often_walks',
    id: 'how_often_walks',
  },
  workSchedule: {
    path: '/chf01/work-schedule',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'work_schedule',
    id: 'work_schedule',
  },
  typicalDayActivity: {
    path: '/chf01/typical-day-activity',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'typical_day_activity',
    id: 'typical_day_activity',
  },
  energyLevels: {
    path: '/chf01/energy-levels',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'energy_levels',
    id: 'energy_levels',
  },
  energyPitch: {
    path: '/chf01/energy-pitch',
    isValidatedStep: true,
    id: 'energy_pitch',
  },
  waterIntake: {
    path: '/chf01/water-intake',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'water_intake',
    id: 'water_intake',
  },
  sleepDuration: {
    path: '/chf01/sleep-duration',
    cluster: 'cluster_lifestyle_habits',
    isValidatedStep: true,
    stateKey: 'sleep_duration',
    id: 'sleep_duration',
  },
  timeBreakfast: {
    path: '/chf01/time-breakfast',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'time_breakfast',
    id: 'time_breakfast',
  },
  timeLunch: {
    path: '/chf01/time-lunch',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'time_lunch',
    id: 'time_lunch',
  },
  timeDinner: {
    path: '/chf01/time-dinner',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'time_dinner',
    id: 'time_dinner',
  },
  diet: {
    path: '/chf01/diet',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'diet',
    id: 'diet',
  },
  habits: {
    path: '/chf01/habits',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'habits',
    id: 'habits',
  },
  cravings: {
    path: '/chf01/cravings',
    cluster: 'cluster_nutrition',
    isValidatedStep: true,
    stateKey: 'cravings',
    id: 'cravings',
  },
  lifeEventsStopped: {
    path: '/chf01/life-events-stopped',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_events_stopped',
    id: 'life_events_stopped',
  },
  mainMotivation: {
    path: '/chf01/main-motivation',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'main_motivation',
    id: 'main_motivation',
  },
  height: {
    path: '/chf01/height',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'height',
    id: 'height',
    required: true,
  },
  weight: {
    path: '/chf01/weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'weight',
    id: 'weight',
    required: true,
  },
  goalWeight: {
    path: '/chf01/goal-weight',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'goal_weight',
    id: 'goal_weight',
    required: true,
  },
  age: {
    path: '/chf01/age',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'age',
    id: 'age',
    required: true,
  },
  wellnessProfile: {
    path: '/chf01/wellness-profile',
    isValidatedStep: true,
    id: 'wellness_profile',
    condition: (data: CHF01DataState) => {
      return (
        isDefined(data.typical_day_activity) &&
        isDefined(data.current_shape) &&
        isDefined(data.how_often_exercise) &&
        isDefined(data.weight_change_exp)
      )
    },
  },
  lifeEvent: {
    path: '/chf01/life-event',
    cluster: 'cluster_almost_there',
    isValidatedStep: true,
    stateKey: 'life_event',
    id: 'life_event',
  },
  lifeEventDate: {
    path: '/chf01/life-event-date',
    isValidatedStep: true,
    stateKey: 'life_event_date',
    id: 'life_event_date',
    condition: (data: CHF01DataState) => {
      return (
        isDefined(data.life_event) &&
        data.life_event !== User.Profile.LifeEvent.NoEvent
      )
    },
  },
  lifeEventGraph: {
    path: '/chf01/life-event-graph',
    isValidatedStep: true,
    id: 'life_event_graph',
    condition: (data: CHF01DataState) => {
      return isDefined(data.goal_weight) && isDefined(data.weight)
    },
  },
  credibilityPitch: {
    path: '/chf01/credibility-pitch',
    isValidatedStep: true,
    id: 'credibility_pitch',
  },
  planGenerator: {
    path: '/chf01/plan-generator',
    id: 'plan_generator',
  },
  email: {
    path: '/chf01/email',
    id: 'email',
    stateKey: 'email',
  },
  name: {
    path: '/chf01/name',
    isValidatedStep: true,
    stateKey: 'displayName',
    id: 'name',
  },
  namePitch: {
    path: '/chf01/name-pitch',
    isValidatedStep: true,
    id: 'name_pitch',
  },
  discountReveal: {
    path: '/chf01/discount-reveal',
    isValidatedStep: true,
    id: 'discount_reveal',
    condition: (_, { promoCode }) => {
      return Boolean(!promoCode || promoCode === DEFAULT_PROMO_CODE)
    },
  },
  ...getSubscriptionFlowPages(Funnel.CHF01),
} as const satisfies FunnelRoutesDefinition<CHF01DataState>['pages']

const pagesMap = Object.values(pages)

const detectLocationPagesMap = [
  pages.planGenerator,
  pages.subscription,
  pages.exclusiveOffer,
] as const satisfies FunnelRoutesDefinition<CHF01DataState>['detectLocationPagesMap']

const noLeaveDialogPagesMap = [
  pages.subscription,
  pages.exclusiveOffer,
  pages.success,
] as const satisfies FunnelRoutesDefinition<CHF01DataState>['noLeaveDialogPagesMap']

export const CHF01_ROUTES = {
  detectLocationPagesMap,
  noLeaveDialogPagesMap,
  pages,
  pagesMap,
} as const satisfies FunnelRoutesDefinition<CHF01DataState>
